<template>
  <div class="account-approval-table-container">
    <div class="d-flex justify-space-between align-center p-4 bg-white border-b">
      <div class="d-flex align-center">
        <span class="text-lg font-semibold mr-4">Account & Journal Approvals</span>
        <v-btn-toggle
          v-model="filters.entityType"
          mandatory
          dense
          color="primary"
          class="mr-4"
          @change="handleFilterChange"
        >
          <v-btn value="" small class="text-none">All</v-btn>
          <v-btn value="ACCOUNT" small class="text-none">Accounts</v-btn>
          <v-btn value="JOURNAL" small class="text-none">Journals</v-btn>
        </v-btn-toggle>
      </div>

      <div class="d-flex align-center">
        <v-select
          v-model="filters.status"
          label="Select Status"
          :items="statusOptions"
          solo
          flat
          clearable
          outlined
          dense
          hide-details
          class="mr-2"
          style="width: 180px"
          @change="handleFilterChange"
        ></v-select>
        <v-btn color="primary" icon @click="handleFilterChange" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="approvals"
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalItems"
      class="table-radius bg-white"
      :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
    >
      <!-- Info Action column -->
      <template v-slot:[`item.viewDetails`]="{ item }">
        <v-btn icon small color="primary" @click="openInfoDialog(item)">
          <v-icon small>mdi-information-outline</v-icon>
        </v-btn>
      </template>

      <!-- Entity & Reference column -->
      <template v-slot:[`item.entityDetails`]="{ item }">
        <div class="p-2" style="min-width: 350px">
          <div class="d-flex align-center">
            <v-chip
              x-small
              label
              class="font-semibold mr-2 flex-shrink-0"
              :color="
                item.entityType === 'ACCOUNT'
                  ? 'teal lighten-5 text-teal-800'
                  : 'purple lighten-5 text-purple-800'
              "
            >
              {{ item.entityType }}
            </v-chip>
            <span class="font-semibold text-blue-600">
              {{ getEntityIdentifier(item) }}
            </span>
          </div>
          <span class="text-xs text-gray-500 mt-1 d-block">
            {{ getEntitySecondaryInfo(item) }}
          </span>
        </div>
      </template>

      <!-- Requested By column -->
      <template v-slot:[`item.requestedBy`]="{ item }">
        <div class="p-2">
          <p class="font-semibold mb-0">
            {{ getRequesterName(item) }}
          </p>
          <span class="text-xs text-gray-500">
            {{ formatDate(item.requestedOn) }}
          </span>
        </div>
      </template>

      <!-- Approval Type column -->
      <template v-slot:[`item.approvalType`]="{ item }">
        <v-chip small label class="font-semibold" :color="getApprovalTypeColor(item.approvalType)">
          {{ formatApprovalType(item.approvalType) }}
        </v-chip>
      </template>

      <!-- Remarks / Reason column -->
      <template v-slot:[`item.remarks`]="{ item }">
        <div class="max-w-xs truncate">
          <span>{{ item.remarks || item.payload?.remarks || '-' }}</span>
        </div>
      </template>

      <!-- Status column -->
      <template v-slot:[`item.status`]="{ item }">
        <v-chip small label class="font-semibold" :color="getStatusColor(item.status)">
          {{ item.status }}
        </v-chip>
      </template>

      <!-- Action column -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center py-2" v-if="item.status === 'PENDING'">
          <v-btn
            small
            color="success"
            class="mr-2 text-none"
            depressed
            @click="openActionDialog(item, 'APPROVE')"
          >
            <v-icon small left>mdi-check</v-icon>
            Approve
          </v-btn>
          <v-btn
            small
            color="error"
            class="text-none"
            outlined
            @click="openActionDialog(item, 'REJECT')"
          >
            <v-icon small left>mdi-close</v-icon>
            Reject
          </v-btn>
        </div>

        <div v-else class="text-xs text-gray-600">
          <span v-if="item.status === 'APPROVED'" class="text-green-600 font-semibold">
            ✓ Approved
          </span>
          <span v-else-if="item.status === 'REJECTED'" class="text-red-600 font-semibold">
            ✕ Rejected: {{ getRejectionReason(item) }}
          </span>
          <span v-else>{{ item.status }}</span>
        </div>
      </template>
    </v-data-table>

    <!-- Confirmation / Remarks Dialog -->
    <v-dialog v-model="dialog.show" max-width="500" persistent>
      <v-card>
        <v-card-title
          class="p-2 text-h6 font-weight-bold d-flex justify-space-between align-center"
        >
          <span>{{ dialog.actionType === 'APPROVE' ? 'Approve Request' : 'Reject Request' }}</span>
          <v-btn icon small @click="dialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="p-4">
          <p class="text-sm text-gray-700">
            Are you sure you want to <strong>{{ dialog.actionType.toLowerCase() }}</strong> this
            {{ dialog.item?.entityType }} request for
            <strong>{{ getEntityIdentifier(dialog.item) }}</strong
            >?
          </p>

          <v-textarea
            v-if="dialog.actionType === 'REJECT'"
            v-model="dialog.rejectionReason"
            label="Rejection Reason *"
            outlined
            dense
            rows="2"
            class="mt-2"
            placeholder="Please specify the reason for rejection..."
            :rules="[(v) => !!v || 'Rejection reason is required']"
          ></v-textarea>

          <v-textarea
            v-model="dialog.remarks"
            label="Reviewer Remarks (Optional)"
            outlined
            dense
            rows="2"
            class="mt-2"
            placeholder="Add any internal remarks..."
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="pt-0 pb-4 px-6 d-flex justify-end">
          <v-btn text class="text-none" @click="dialog.show = false">Cancel</v-btn>
          <v-btn
            :color="dialog.actionType === 'APPROVE' ? 'success' : 'error'"
            class="text-none px-4"
            depressed
            :loading="dialog.submitting"
            :disabled="dialog.actionType === 'REJECT' && !dialog.rejectionReason"
            @click="submitAction"
          >
            Confirm {{ dialog.actionType === 'APPROVE' ? 'Approval' : 'Rejection' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details / Diff Dialog -->
    <v-dialog v-model="infoDialog.show" max-width="850" scrollable>
      <v-card class="info-dialog-card" v-if="infoDialog.item">
        <!-- Header -->
        <v-card-title class="p-4 bg-gray-50 border-b d-flex justify-space-between align-center">
          <div class="d-flex align-center flex-wrap gap-2">
            <v-icon
              class="mr-2"
              :color="
                infoDialog.item.entityType === 'ACCOUNT' ? 'teal darken-1' : 'purple darken-1'
              "
            >
              {{ infoDialog.item.entityType === 'ACCOUNT' ? 'mdi-bank' : 'mdi-book-open-variant' }}
            </v-icon>
            <span class="text-h6 font-weight-bold text-gray-800">
              {{
                infoDialog.item.entityType === 'ACCOUNT'
                  ? 'Account Request Details'
                  : 'Journal Voucher Details'
              }}
            </span>
            <v-chip
              x-small
              label
              class="font-semibold ml-2"
              :color="
                infoDialog.item.entityType === 'ACCOUNT'
                  ? 'teal lighten-5 text-teal-800'
                  : 'purple lighten-5 text-purple-800'
              "
            >
              {{ infoDialog.item.entityType }}
            </v-chip>
            <v-chip
              x-small
              label
              class="font-semibold"
              :color="getApprovalTypeColor(infoDialog.item.approvalType)"
            >
              {{ formatApprovalType(infoDialog.item.approvalType) }}
            </v-chip>
            <v-chip
              x-small
              label
              class="font-semibold"
              :color="getStatusColor(infoDialog.item.status)"
            >
              {{ infoDialog.item.status }}
            </v-chip>
          </div>
          <v-btn icon small @click="infoDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Scrollable Content -->
        <v-card-text class="p-6" style="max-height: 75vh">
          <!-- Overview Hero Card -->
          <div class="overview-banner p-4 rounded-lg border mb-5">
            <div class="d-flex justify-space-between align-start flex-wrap">
              <div>
                <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {{
                    infoDialog.item.entityType === 'ACCOUNT'
                      ? 'Target Account'
                      : 'Voucher Reference'
                  }}
                </span>
                <h2 class="text-xl font-bold text-gray-900 mt-1">
                  {{ getEntityIdentifier(infoDialog.item) }}
                </h2>
                <div class="text-sm text-gray-600 mt-1 d-flex align-center flex-wrap gap-2">
                  <span v-if="getEntitySecondaryInfo(infoDialog.item)" class="font-medium">
                    {{ getEntitySecondaryInfo(infoDialog.item) }}
                  </span>
                  <span v-if="infoDialog.item._id" class="text-xs text-gray-400">
                    ID: {{ infoDialog.item._id }}
                  </span>
                </div>
              </div>

              <!-- Quick Status Pill / Badge -->
              <div class="text-right mt-2 mt-sm-0">
                <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider d-block">
                  Current Status
                </span>
                <span
                  class="inline-block mt-1 px-3 py-1 text-xs font-bold rounded-full"
                  :class="{
                    'bg-amber-100 text-amber-800 border border-amber-300':
                      infoDialog.item.status === 'PENDING',
                    'bg-emerald-100 text-emerald-800 border border-emerald-300':
                      infoDialog.item.status === 'APPROVED',
                    'bg-rose-100 text-rose-800 border border-rose-300':
                      infoDialog.item.status === 'REJECTED'
                  }"
                >
                  <v-icon
                    x-small
                    left
                    :color="
                      infoDialog.item.status === 'APPROVED'
                        ? 'green darken-2'
                        : infoDialog.item.status === 'REJECTED'
                        ? 'red darken-2'
                        : 'orange darken-2'
                    "
                  >
                    {{
                      infoDialog.item.status === 'APPROVED'
                        ? 'mdi-check-circle'
                        : infoDialog.item.status === 'REJECTED'
                        ? 'mdi-close-circle'
                        : 'mdi-clock-outline'
                    }}
                  </v-icon>
                  {{ infoDialog.item.status }}
                </span>
              </div>
            </div>

            <!-- Meta Grid: Requester & Timing -->
            <div class="grid-meta-row mt-4 pt-3 border-t">
              <div class="row ma-0">
                <div class="col-12 col-sm-4 pa-1">
                  <div class="text-xs text-gray-500 font-medium">Requested By</div>
                  <div class="text-sm font-semibold text-gray-800 d-flex align-center mt-1">
                    <v-icon x-small class="mr-1 text-gray-400">mdi-account</v-icon>
                    {{ getRequesterName(infoDialog.item) }}
                  </div>
                </div>
                <div class="col-12 col-sm-4 pa-1">
                  <div class="text-xs text-gray-500 font-medium">Requested On</div>
                  <div class="text-sm font-semibold text-gray-800 d-flex align-center mt-1">
                    <v-icon x-small class="mr-1 text-gray-400">mdi-calendar-clock</v-icon>
                    {{ formatDate(infoDialog.item.requestedOn) }}
                  </div>
                </div>
                <div class="col-12 col-sm-4 pa-1">
                  <div class="text-xs text-gray-500 font-medium">Request Type</div>
                  <div class="text-sm font-semibold text-gray-800 d-flex align-center mt-1">
                    <v-icon x-small class="mr-1 text-gray-400">mdi-tag-outline</v-icon>
                    {{ formatApprovalType(infoDialog.item.approvalType) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Requester Remarks -->
            <div
              v-if="infoDialog.item.remarks || infoDialog.item.payload?.remarks"
              class="mt-3 p-2 bg-white rounded border text-xs text-gray-700 d-flex align-start"
            >
              <v-icon x-small color="primary" class="mr-2 mt-1">mdi-comment-text-outline</v-icon>
              <div>
                <span class="font-semibold text-gray-900">Requester Remarks: </span>
                {{ infoDialog.item.remarks || infoDialog.item.payload?.remarks }}
              </div>
            </div>
          </div>

          <!-- Rejection Banner if Rejected -->
          <div
            v-if="infoDialog.item.status === 'REJECTED'"
            class="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-900"
          >
            <div class="d-flex align-center font-bold text-red-800 mb-1">
              <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
              Rejection Details
            </div>
            <p class="mb-1"><strong>Reason:</strong> {{ getRejectionReason(infoDialog.item) }}</p>
            <p v-if="getApproverRemarks(infoDialog.item)" class="mb-0">
              <strong>Reviewer Remarks:</strong> {{ getApproverRemarks(infoDialog.item) }}
            </p>
          </div>

          <!-- Approval Banner if Approved -->
          <div
            v-if="infoDialog.item.status === 'APPROVED'"
            class="mb-5 p-3 rounded-lg bg-green-50 border border-green-200 text-xs text-green-900"
          >
            <div class="d-flex align-center font-bold text-green-800 mb-1">
              <v-icon small color="success" class="mr-1">mdi-check-decagram</v-icon>
              Approval Information
            </div>
            <p class="mb-0">
              Approved on {{ formatDate(getApprovedDate(infoDialog.item)) }}
              <span v-if="getApproverRemarks(infoDialog.item)">
                — <strong>Remarks:</strong> {{ getApproverRemarks(infoDialog.item) }}
              </span>
            </p>
          </div>

          <!-- =================== ENTITY: ACCOUNT VIEW =================== -->
          <div v-if="infoDialog.item.entityType === 'ACCOUNT'">
            <!-- Modified Fields Diff (For UPDATE) -->
            <div
              v-if="
                isUpdateAction(infoDialog.item.approvalType) &&
                getAccountDiffList(infoDialog.item).length > 0
              "
              class="mb-5"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-sm font-bold text-gray-800 d-flex align-center">
                  <v-icon small color="amber darken-2" class="mr-1">mdi-compare-horizontal</v-icon>
                  Modified Changes (Comparison)
                </span>
                <span class="text-xs text-gray-500">
                  {{ getAccountDiffList(infoDialog.item).length }} field(s) updated
                </span>
              </div>

              <div class="border rounded-lg overflow-hidden">
                <table class="w-full diff-table text-xs">
                  <thead class="bg-gray-100 text-gray-700">
                    <tr>
                      <th class="text-left py-2 px-3">Field</th>
                      <th class="text-left py-2 px-3">Previous Value (Snapshot)</th>
                      <th class="text-left py-2 px-3">New Value (Requested)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(diff, idx) in getAccountDiffList(infoDialog.item)"
                      :key="`diff-${idx}`"
                      class="border-t hover:bg-gray-50"
                    >
                      <td class="py-2 px-3 font-semibold text-gray-700">
                        {{ diff.label }}
                      </td>
                      <td class="py-2 px-3 text-red-700 bg-red-50/50">
                        <span v-if="diff.oldValue">{{ diff.oldValue }}</span>
                        <span v-else class="text-gray-400 italic font-normal">None</span>
                      </td>
                      <td class="py-2 px-3 text-green-700 bg-green-50/50 font-semibold">
                        <div class="d-flex align-center">
                          <v-icon x-small color="green darken-1" class="mr-1"
                            >mdi-arrow-right</v-icon
                          >
                          <span>{{ diff.newValue }}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Account Details Card Grid -->
            <div class="row">
              <!-- General Info Card -->
              <div class="col-12 col-md-6 pa-2">
                <div class="p-4 rounded-lg border bg-white h-100">
                  <h3
                    class="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3 d-flex align-center"
                  >
                    <v-icon x-small color="primary" class="mr-1">mdi-information</v-icon>
                    Account Information
                  </h3>
                  <div class="detail-rows text-xs">
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Account Name:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{ getAccountFieldValue(infoDialog.item, 'name') || 'N/A' }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Account Code:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{ getAccountFieldValue(infoDialog.item, 'code') || 'N/A' }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Category / Type:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{
                          getAccountFieldValue(infoDialog.item, 'category') ||
                          getAccountFieldValue(infoDialog.item, 'accountType') ||
                          'N/A'
                        }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Opening Balance:</span>
                      <span class="font-bold text-emerald-700 text-right">
                        ₹{{
                          formatCurrency(getAccountFieldValue(infoDialog.item, 'openingBalance'))
                        }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Currency:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{ getAccountFieldValue(infoDialog.item, 'currency') || 'INR' }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 d-flex justify-space-between">
                      <span class="text-gray-500">Description / Notes:</span>
                      <span class="font-medium text-gray-800 text-right max-w-xs">
                        {{
                          getAccountFieldValue(infoDialog.item, 'description') ||
                          getAccountFieldValue(infoDialog.item, 'notes') ||
                          '-'
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Contact & Tax Info Card -->
              <div class="col-12 col-md-6 pa-2">
                <div class="p-4 rounded-lg border bg-white h-100">
                  <h3
                    class="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3 d-flex align-center"
                  >
                    <v-icon x-small color="primary" class="mr-1"
                      >mdi-card-account-details-outline</v-icon
                    >
                    Contact & Tax Details
                  </h3>
                  <div class="detail-rows text-xs">
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Mailing Name:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{ getAccountFieldValue(infoDialog.item, 'mailingName') || '-' }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Contact Person:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{ getAccountFieldValue(infoDialog.item, 'contactPerson') || '-' }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Contact Number:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{
                          getAccountFieldValue(infoDialog.item, 'mobileNo') ||
                          getAccountFieldValue(infoDialog.item, 'phoneNo') ||
                          '-'
                        }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">Email:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{ getAccountFieldValue(infoDialog.item, 'email') || '-' }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 border-b d-flex justify-space-between">
                      <span class="text-gray-500">PAN / IT No:</span>
                      <span class="font-semibold text-gray-900 text-right">
                        {{ getAccountFieldValue(infoDialog.item, 'panItNo') || '-' }}
                      </span>
                    </div>
                    <div class="detail-row py-1-5 d-flex justify-space-between">
                      <span class="text-gray-500">Address:</span>
                      <span class="font-medium text-gray-800 text-right max-w-xs">
                        {{
                          getFormattedAddress(getAccountFieldValue(infoDialog.item, 'address')) ||
                          '-'
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- =================== ENTITY: JOURNAL VIEW =================== -->
          <div v-else-if="infoDialog.item.entityType === 'JOURNAL'">
            <!-- Journal Voucher Header Info Card -->
            <div class="p-4 rounded-lg border bg-white mb-4">
              <div class="row">
                <div class="col-12 col-sm-3 pa-2 border-r">
                  <div class="text-xs text-gray-500 font-medium">Voucher Number</div>
                  <div class="text-sm font-bold text-gray-900 mt-1">
                    {{ getJournalFieldValue(infoDialog.item, 'voucherNo') || 'N/A' }}
                  </div>
                </div>
                <div class="col-12 col-sm-3 pa-2 border-r">
                  <div class="text-xs text-gray-500 font-medium">Voucher Type</div>
                  <div class="text-sm font-semibold text-gray-900 mt-1">
                    {{ getJournalFieldValue(infoDialog.item, 'voucherType') || 'Journal' }}
                  </div>
                </div>
                <div class="col-12 col-sm-3 pa-2 border-r">
                  <div class="text-xs text-gray-500 font-medium">Transaction Date</div>
                  <div class="text-sm font-semibold text-gray-900 mt-1">
                    {{ formatDate(getJournalFieldValue(infoDialog.item, 'date')) }}
                  </div>
                </div>
                <div class="col-12 col-sm-3 pa-2">
                  <div class="text-xs text-gray-500 font-medium">Total Amount</div>
                  <div class="text-base font-bold text-emerald-700 mt-1">
                    ₹{{ formatCurrency(getJournalTotalAmount(infoDialog.item)) }}
                  </div>
                </div>
              </div>

              <div
                v-if="
                  getJournalFieldValue(infoDialog.item, 'narration') ||
                  getJournalFieldValue(infoDialog.item, 'description')
                "
                class="mt-3 pt-3 border-t text-xs text-gray-700"
              >
                <span class="font-semibold text-gray-900">Narration / Description: </span>
                {{
                  getJournalFieldValue(infoDialog.item, 'narration') ||
                  getJournalFieldValue(infoDialog.item, 'description')
                }}
              </div>
            </div>

            <!-- Journal Opposite Legs / Accounting Breakdown Table -->
            <div class="mb-4">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-sm font-bold text-gray-800 d-flex align-center">
                  <v-icon small color="purple darken-2" class="mr-1"
                    >mdi-format-list-numbered</v-icon
                  >
                  Accounting Legs & Entries
                </span>
                <span class="text-xs text-gray-500">
                  {{ getJournalLegs(infoDialog.item).length }} Entry Leg(s)
                </span>
              </div>

              <div class="border rounded-lg overflow-hidden bg-white">
                <table class="w-full text-xs legs-table">
                  <thead class="bg-gray-100 text-gray-700 font-semibold border-b">
                    <tr>
                      <th class="text-left py-2 px-3" style="width: 40px">#</th>
                      <th class="text-left py-2 px-3">Particulars / Account</th>
                      <th class="text-right py-2 px-3" style="width: 140px">Debit (₹)</th>
                      <th class="text-right py-2 px-3" style="width: 140px">Credit (₹)</th>
                      <th class="text-left py-2 px-3">Ref / Inst No</th>
                      <th class="text-left py-2 px-3">Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(leg, idx) in getJournalLegs(infoDialog.item)"
                      :key="`leg-${idx}`"
                      class="border-b hover:bg-gray-50"
                    >
                      <td class="py-2 px-3 text-gray-500">{{ idx + 1 }}</td>
                      <td class="py-2 px-3 font-semibold text-gray-900">
                        {{
                          leg.oppositeParticulars ||
                          leg.particulars ||
                          leg.accountName ||
                          `Leg ${idx + 1}`
                        }}
                      </td>
                      <td class="py-2 px-3 text-right font-medium text-blue-700">
                        <span v-if="Number(leg.debit || 0) > 0">
                          ₹{{ formatCurrency(leg.debit) }}
                        </span>
                        <span v-else class="text-gray-400">-</span>
                      </td>
                      <td class="py-2 px-3 text-right font-medium text-emerald-700">
                        <span v-if="Number(leg.credit || 0) > 0">
                          ₹{{ formatCurrency(leg.credit) }}
                        </span>
                        <span v-else class="text-gray-400">-</span>
                      </td>
                      <td class="py-2 px-3 text-gray-600">
                        {{ leg.instrumentNo || '-' }}
                      </td>
                      <td class="py-2 px-3 text-gray-600">
                        {{ leg.comments || '-' }}
                      </td>
                    </tr>
                    <tr v-if="getJournalLegs(infoDialog.item).length === 0">
                      <td colspan="6" class="py-4 text-center text-gray-500 italic">
                        No distinct line legs provided in payload. Total voucher amount: ₹{{
                          formatCurrency(getJournalTotalAmount(infoDialog.item))
                        }}
                      </td>
                    </tr>
                  </tbody>
                  <!-- Totals Footer -->
                  <tfoot
                    v-if="getJournalLegs(infoDialog.item).length > 0"
                    class="bg-gray-50 font-bold border-t"
                  >
                    <tr>
                      <td colspan="2" class="py-2 px-3 text-right uppercase text-gray-700">
                        Totals:
                      </td>
                      <td class="py-2 px-3 text-right text-blue-800">
                        ₹{{ formatCurrency(getJournalLegsTotal(infoDialog.item, 'debit')) }}
                      </td>
                      <td class="py-2 px-3 text-right text-emerald-800">
                        ₹{{ formatCurrency(getJournalLegsTotal(infoDialog.item, 'credit')) }}
                      </td>
                      <td colspan="2" class="py-2 px-3 text-xs font-normal text-gray-500">
                        <v-chip
                          x-small
                          label
                          :color="
                            isJournalBalanced(infoDialog.item)
                              ? 'green lighten-5 text-green-700'
                              : 'amber lighten-5 text-amber-800'
                          "
                        >
                          {{
                            isJournalBalanced(infoDialog.item) ? '✓ Balanced' : 'Unbalanced Legs'
                          }}
                        </v-chip>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <!-- Approver Audit Trail / History -->
          <div
            v-if="infoDialog.item.approvers && infoDialog.item.approvers.length > 0"
            class="mt-4 pt-3 border-t"
          >
            <span class="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2 d-block">
              Approver Workflow History
            </span>
            <div class="space-y-2">
              <div
                v-for="(approver, aIdx) in infoDialog.item.approvers"
                :key="`approver-${aIdx}`"
                class="p-2-5 rounded border bg-gray-50 text-xs d-flex justify-space-between align-center flex-wrap"
              >
                <div>
                  <div class="font-semibold text-gray-800">
                    {{ getApproverUserLabel(approver) }}
                  </div>
                  <div v-if="approver.approvedOn" class="text-gray-500 text-xxs">
                    Action Taken: {{ formatDate(approver.approvedOn) }}
                  </div>
                  <div v-if="approver.rejectionReason" class="text-red-700 mt-1">
                    <strong>Reason:</strong> {{ approver.rejectionReason }}
                  </div>
                  <div v-if="approver.remarks" class="text-gray-600 mt-1">
                    <strong>Remarks:</strong> {{ approver.remarks }}
                  </div>
                </div>
                <div class="mt-1 mt-sm-0">
                  <v-chip
                    x-small
                    label
                    class="font-semibold"
                    :color="getStatusColor(approver.userAction || approver.status || 'PENDING')"
                  >
                    {{ approver.userAction || approver.status || 'PENDING' }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>

          <!-- Technical Raw JSON Details Accordion -->
          <v-expansion-panels class="mt-4" flat>
            <v-expansion-panel class="border rounded-lg overflow-hidden">
              <v-expansion-panel-header
                class="text-xs font-semibold text-gray-600 py-2 px-3 bg-gray-50"
              >
                <div class="d-flex align-center">
                  <v-icon x-small class="mr-2 text-gray-400">mdi-code-json</v-icon>
                  <span>Technical Data (Raw JSON Payload & Snapshot)</span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-3">
                <div class="mb-3">
                  <span class="text-xxs font-bold text-gray-500 uppercase">Payload JSON:</span>
                  <pre
                    class="bg-gray-900 text-gray-100 p-3 rounded text-xs mt-1 overflow-auto max-h-48"
                    >{{ JSON.stringify(infoDialog.item.payload || {}, null, 2) }}</pre
                  >
                </div>
                <div v-if="infoDialog.item.snapshot">
                  <span class="text-xxs font-bold text-gray-500 uppercase">Snapshot JSON:</span>
                  <pre
                    class="bg-gray-900 text-gray-100 p-3 rounded text-xs mt-1 overflow-auto max-h-48"
                    >{{ JSON.stringify(infoDialog.item.snapshot || {}, null, 2) }}</pre
                  >
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <!-- Actions Footer -->
        <v-divider></v-divider>
        <v-card-actions class="px-4 bg-gray-50 d-flex justify-space-between align-center">
          <div>
            <span class="text-xs text-gray-500">
              Status:
              <strong :class="getStatusTextColor(infoDialog.item.status)">{{
                infoDialog.item.status
              }}</strong>
            </span>
          </div>

          <div class="d-flex align-center">
            <!-- Inline Approve / Reject Buttons if Pending -->
            <template v-if="infoDialog.item.status === 'PENDING'">
              <v-btn
                small
                color="error"
                outlined
                class="mr-2 text-none"
                @click="openActionFromInfo('REJECT')"
              >
                <v-icon small left>mdi-close</v-icon>
                Reject
              </v-btn>
              <v-btn
                small
                color="success"
                depressed
                class="mr-2 text-none"
                @click="openActionFromInfo('APPROVE')"
              >
                <v-icon small left>mdi-check</v-icon>
                Approve
              </v-btn>
            </template>

            <v-btn small depressed class="text-none" @click="infoDialog.show = false">
              Close
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'AccountApprovalTable',
  data() {
    return {
      approvals: [],
      loading: false,
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      filters: {
        entityType: '',
        status: ''
      },
      headers: [
        { text: 'Info', value: 'viewDetails', sortable: false, width: '60px' },
        { text: 'Entity / Reference', value: 'entityDetails', sortable: false, width: '500px' },
        { text: 'Requested By', value: 'requestedBy', sortable: false, width: '180px' },
        { text: 'Approval Type', value: 'approvalType', sortable: false, width: '150px' },
        { text: 'Remarks', value: 'remarks', sortable: false },
        { text: 'Status', value: 'status', sortable: false, width: '110px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '190px' }
      ],
      statusOptions: [
        { text: 'All', value: '' },
        { text: 'Pending', value: 'PENDING' },
        { text: 'Approved', value: 'APPROVED' },
        { text: 'Rejected', value: 'REJECTED' }
      ],
      dialog: {
        show: false,
        actionType: 'APPROVE',
        item: null,
        rejectionReason: '',
        remarks: '',
        submitting: false
      },
      infoDialog: {
        show: false,
        item: null
      }
    }
  },
  mounted() {
    this.fetchApprovals()
  },
  watch: {
    options: {
      handler() {
        this.fetchApprovals()
      },
      deep: true
    }
  },
  methods: {
    handleFilterChange() {
      this.options.page = 1
      this.fetchApprovals()
    },
    async fetchApprovals() {
      this.loading = true
      try {
        const { page = 1, itemsPerPage = 10 } = this.options || {}
        const params = {
          page,
          limit: itemsPerPage,
          entityType: this.filters.entityType || undefined,
          status: this.filters.status || undefined
        }
        const response = await this.$axios.get('/orgSetup/account-management-approvals', { params })
        this.approvals = response.data?.approvals || []
        this.totalItems = response.data?.pagination?.totalCount || this.approvals.length || 0
      } catch (error) {
        console.error('Error fetching account approvals:', error)
      } finally {
        this.loading = false
      }
    },
    getEntityIdentifier(item) {
      if (!item) return 'N/A'
      if (item.entityType === 'ACCOUNT') {
        return item.accountDetails?.name || item.payload?.name || item.snapshot?.name || 'Account'
      } else if (item.entityType === 'JOURNAL') {
        return (
          item.journalDetails?.voucherNo ||
          item.payload?.voucherNo ||
          item.snapshot?.voucherNo ||
          'Journal'
        )
      }
      return item.entityType || 'N/A'
    },
    getEntitySecondaryInfo(item) {
      if (!item) return ''
      if (item.entityType === 'ACCOUNT') {
        const code = item.accountDetails?.code || item.payload?.code || item.snapshot?.code
        const type =
          item.accountDetails?.accountType ||
          item.payload?.accountType ||
          item.payload?.category ||
          item.snapshot?.accountType ||
          item.snapshot?.category
        return [code ? `Code: ${code}` : '', type ? `Type: ${type}` : '']
          .filter(Boolean)
          .join(' • ')
      } else if (item.entityType === 'JOURNAL') {
        const amount = this.getJournalTotalAmount(item)
        const date = item.payload?.date || item.snapshot?.date || item.journalDetails?.date
        return [amount ? `₹${this.formatCurrency(amount)}` : '', date ? this.formatDate(date) : '']
          .filter(Boolean)
          .join(' • ')
      }
      return ''
    },
    getRequesterName(item) {
      if (!item) return 'Unknown'
      const req =
        item.requesterDetails || (typeof item.requestedBy === 'object' ? item.requestedBy : null)
      if (req) {
        return `${req.firstName || ''} ${req.lastName || ''}`.trim() || req.email || 'Requester'
      }
      return 'Unknown'
    },
    getRejectionReason(item) {
      const approverWithRejection = item?.approvers?.find?.(
        (a) => a.userAction === 'REJECTED' || a.rejectionReason
      )
      return approverWithRejection?.rejectionReason || 'Rejected by reviewer'
    },
    getApproverRemarks(item) {
      const approverWithRemarks = item?.approvers?.find?.((a) => a.remarks)
      return approverWithRemarks?.remarks || ''
    },
    getApprovedDate(item) {
      const approved = item?.approvers?.find?.((a) => a.userAction === 'APPROVED' && a.approvedOn)
      return approved?.approvedOn || item?.updatedAt || null
    },
    getApproverUserLabel(approver) {
      if (!approver) return 'Reviewer'
      const u = approver.userId
      if (typeof u === 'object' && u !== null) {
        return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Reviewer'
      }
      return `User (${approver.userId || 'Reviewer'})`
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return this.$dayjs
        ? this.$dayjs(date).format('DD/MM/YYYY hh:mm A')
        : new Date(date).toLocaleDateString()
    },
    formatCurrency(val) {
      if (val === null || val === undefined || val === '') return '0.00'
      const num = Number(val)
      if (isNaN(num)) return val
      return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatApprovalType(type) {
      if (!type) return 'N/A'
      return type.replace(/_/g, ' ')
    },
    isUpdateAction(type) {
      if (!type) return false
      return type.includes('UPDATE') || type.includes('EDIT')
    },
    getApprovalTypeColor(type) {
      if (!type) return 'grey lighten-4 text-gray-700'
      if (type.includes('DELETE')) return 'red lighten-5 text-red-700'
      if (type.includes('UPDATE') || type.includes('EDIT')) return 'amber lighten-5 text-amber-800'
      if (type.includes('CREATE')) return 'green lighten-5 text-green-700'
      return 'blue lighten-5 text-blue-700'
    },
    getStatusColor(status) {
      switch (status) {
        case 'APPROVED':
          return 'green lighten-5 text-green-700'
        case 'REJECTED':
          return 'red lighten-5 text-red-700'
        case 'PENDING':
          return 'orange lighten-5 text-orange-700'
        default:
          return 'grey lighten-4 text-gray-700'
      }
    },
    getStatusTextColor(status) {
      switch (status) {
        case 'APPROVED':
          return 'text-green-600'
        case 'REJECTED':
          return 'text-red-600'
        case 'PENDING':
          return 'text-amber-600'
        default:
          return 'text-gray-600'
      }
    },
    getAccountFieldValue(item, field) {
      if (!item) return ''
      return item.payload?.[field] ?? item.snapshot?.[field] ?? item.accountDetails?.[field] ?? ''
    },
    getFormattedAddress(addr) {
      if (!addr) return ''
      if (typeof addr === 'string') return addr
      const parts = [
        addr.addressLine1,
        addr.addressLine2,
        addr.city,
        addr.state,
        addr.country,
        addr.pincode
      ].filter(Boolean)
      return parts.join(', ')
    },
    getAccountDiffList(item) {
      if (!item) return []
      const payload = item.payload || {}
      const snapshot = item.snapshot || {}
      const fields = [
        { key: 'name', label: 'Account Name' },
        { key: 'code', label: 'Account Code' },
        { key: 'category', label: 'Category / Type' },
        { key: 'accountType', label: 'Account Type' },
        { key: 'currency', label: 'Currency' },
        { key: 'openingBalance', label: 'Opening Balance', isCurrency: true },
        { key: 'mailingName', label: 'Mailing Name' },
        { key: 'contactPerson', label: 'Contact Person' },
        { key: 'mobileNo', label: 'Mobile Number' },
        { key: 'phoneNo', label: 'Phone Number' },
        { key: 'email', label: 'Email' },
        { key: 'panItNo', label: 'PAN / IT No' },
        { key: 'description', label: 'Description' }
      ]

      const diffs = []
      fields.forEach((f) => {
        const newVal = payload[f.key]
        const oldVal = snapshot[f.key]
        if (newVal !== undefined && newVal !== oldVal) {
          diffs.push({
            label: f.label,
            oldValue: f.isCurrency
              ? oldVal !== undefined
                ? `₹${this.formatCurrency(oldVal)}`
                : ''
              : String(oldVal || ''),
            newValue: f.isCurrency ? `₹${this.formatCurrency(newVal)}` : String(newVal || '')
          })
        }
      })
      return diffs
    },
    getJournalFieldValue(item, field) {
      if (!item) return ''
      return item.payload?.[field] ?? item.snapshot?.[field] ?? item.journalDetails?.[field] ?? ''
    },
    getJournalTotalAmount(item) {
      if (!item) return 0
      const p = item.payload || {}
      const s = item.snapshot || {}
      const j = item.journalDetails || {}
      return (
        p.totalAmount ??
        p.amount ??
        p.totalDebit ??
        s.totalAmount ??
        s.amount ??
        j.debit ??
        j.credit ??
        0
      )
    },
    getJournalLegs(item) {
      if (!item) return []
      if (Array.isArray(item.payload?.oppositeLegs) && item.payload.oppositeLegs.length > 0) {
        return item.payload.oppositeLegs
      }
      if (Array.isArray(item.snapshot?.oppositeLegs) && item.snapshot.oppositeLegs.length > 0) {
        return item.snapshot.oppositeLegs
      }
      return []
    },
    getJournalLegsTotal(item, type) {
      const legs = this.getJournalLegs(item)
      return legs.reduce((sum, leg) => sum + Number(leg[type] || 0), 0)
    },
    isJournalBalanced(item) {
      const debitTotal = this.getJournalLegsTotal(item, 'debit')
      const creditTotal = this.getJournalLegsTotal(item, 'credit')
      return Math.abs(debitTotal - creditTotal) < 0.01
    },
    openInfoDialog(item) {
      this.infoDialog.item = item
      this.infoDialog.show = true
    },
    openActionFromInfo(actionType) {
      const item = this.infoDialog.item
      this.infoDialog.show = false
      this.openActionDialog(item, actionType)
    },
    openActionDialog(item, actionType) {
      this.dialog.item = item
      this.dialog.actionType = actionType
      this.dialog.rejectionReason = ''
      this.dialog.remarks = ''
      this.dialog.submitting = false
      this.dialog.show = true
    },
    async submitAction() {
      if (!this.dialog.item) return
      this.dialog.submitting = true
      try {
        const id = this.dialog.item._id
        const isApprove = this.dialog.actionType === 'APPROVE'
        const endpoint = `/orgSetup/account-management-approvals/${id}/${
          isApprove ? 'approve' : 'reject'
        }`
        const payload = isApprove
          ? { remarks: this.dialog.remarks }
          : {
              rejectionReason: this.dialog.rejectionReason,
              remarks: this.dialog.remarks
            }

        await this.$axios.post(endpoint, payload)
        this.$toast.success(`Request ${isApprove ? 'approved' : 'rejected'} successfully!`)
        this.dialog.show = false
        await this.fetchApprovals()
      } catch (error) {
        console.error('Error processing approval action:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to process request.')
      } finally {
        this.dialog.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.account-approval-table-container {
  border-radius: 8px;
  overflow: hidden;
}
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.info-dialog-card {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
}
.overview-banner {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-color: #e2e8f0;
}
.diff-table th,
.diff-table td {
  vertical-align: middle;
}
.legs-table th,
.legs-table td {
  vertical-align: middle;
}
.py-1-5 {
  padding-top: 6px;
  padding-bottom: 6px;
}
.p-2-5 {
  padding: 10px;
}
.text-xxs {
  font-size: 11px;
}
.gap-2 {
  gap: 8px;
}
.space-y-2 > * + * {
  margin-top: 8px;
}
</style>
